import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import TimelineIcon from '@mui/icons-material/Timeline';
import Chart, { useChart } from '../chart';

// ---------------------------------------------------------------------------

const MAX_HISTORY = 120; // 4 minutes at 2s polling

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

function pmQuality(value) {
  if (value <= 12) return { label: 'Good', color: 'success' };
  if (value <= 35.4) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

function co2Quality(value) {
  if (value <= 800) return { label: 'Good', color: 'success' };
  if (value <= 1000) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

// First-pass gas bands (tunable). Exported so they can be unit-tested and
// reused. CO / mercaptan are ppm; NOx is a unitless Sensirion index.
export const CO_GOOD_MAX = 9;
export const CO_ELEVATED_MAX = 35;
export const NOX_GOOD_MAX = 20;
export const NOX_ELEVATED_MAX = 150;
// VOC Index is the Sensirion 1..500 scale, centred on 100 ("typical
// conditions"), so the good band has to sit above the baseline rather than
// near zero the way a ppm band does.
export const VOC_INDEX_GOOD_MAX = 150;
export const VOC_INDEX_ELEVATED_MAX = 250;
export const MERC_GOOD_MAX = 1;
export const MERC_ELEVATED_MAX = 10;

export function coQuality(value) {
  if (value < CO_GOOD_MAX) return { label: 'Good', color: 'success' };
  if (value <= CO_ELEVATED_MAX) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

export function noxQuality(value) {
  if (value < NOX_GOOD_MAX) return { label: 'Good', color: 'success' };
  if (value <= NOX_ELEVATED_MAX) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

export function vocIndexQuality(value) {
  if (value < VOC_INDEX_GOOD_MAX) return { label: 'Good', color: 'success' };
  if (value <= VOC_INDEX_ELEVATED_MAX) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

export function mercQuality(value) {
  if (value < MERC_GOOD_MAX) return { label: 'Good', color: 'success' };
  if (value <= MERC_ELEVATED_MAX) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

function formatTime(date) {
  return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ---------------------------------------------------------------------------

function ReadingCell({ label, value, unit = undefined, quality = null, reserveQuality = false }) {
  const display = isNum(value) ? value : '—';
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{display}{unit && isNum(value) && <Typography component="span" variant="body2" color="text.secondary"> {unit}</Typography>}</Typography>
        {/* Reserve the chip's row even before data so the cell doesn't grow
            when the quality chip first appears. */}
        {(quality || reserveQuality) && (
          <Box sx={{ minHeight: 24, mt: 0.25 }}>
            {quality && <Chip label={quality.label} color={quality.color} size="small" />}
          </Box>
        )}
      </Box>
    </Grid>
  );
}

ReadingCell.propTypes = {
  label: PropTypes.string.isRequired,
   
  value: PropTypes.any,
  unit: PropTypes.string,
  quality: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
  reserveQuality: PropTypes.bool,
};

function ReadingsGrid({ readings }) {
  const r = readings || {};
  return (
    <>
      <Typography variant="overline" color="text.secondary">Mass Concentration</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <ReadingCell label="PM1.0" value={r.pm1} unit="µg/m³" />
        <ReadingCell label="PM2.5" value={r.pm25} unit="µg/m³" reserveQuality quality={isNum(r.pm25) ? pmQuality(r.pm25) : null} />
        <ReadingCell label="PM10" value={r.pm10} unit="µg/m³" />
        <ReadingCell label="CO2" value={r.co2} unit="ppm" reserveQuality quality={isNum(r.co2) ? co2Quality(r.co2) : null} />
      </Grid>
      <Typography variant="overline" color="text.secondary">Environment</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <ReadingCell label="Temperature" value={r.temp} unit="°C" />
        <ReadingCell label="Humidity" value={r.humidity} unit="%" />
        <ReadingCell label="VOC" value={r.voc} unit="ppm" />
      </Grid>
      <Typography variant="overline" color="text.secondary">Particle Counts (per 100cm³)</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <ReadingCell label=">0.3 µm" value={r.pc03} />
        <ReadingCell label=">0.5 µm" value={r.pc05} />
        <ReadingCell label=">1.0 µm" value={r.pc10} />
        <ReadingCell label=">2.5 µm" value={r.pc25} />
        <ReadingCell label=">5.0 µm" value={r.pc50} />
        <ReadingCell label=">10 µm" value={r.pc100} />
      </Grid>
      {/* Gas channels — only present on VPSIAIRPLUS hardware. Cells render "—"
          when the channel is absent (raw/ppm null), matching the skeleton idiom. */}
      <Typography variant="overline" color="text.secondary">Gas</Typography>
      <Grid container spacing={2}>
        <ReadingCell label="Carbon Monoxide" value={r.coPpm} unit="ppm" reserveQuality quality={isNum(r.coPpm) ? coQuality(r.coPpm) : null} />
        <ReadingCell label="VOC Index" value={r.vocIndex} reserveQuality quality={isNum(r.vocIndex) ? vocIndexQuality(r.vocIndex) : null} />
        <ReadingCell label="NOx" value={r.nox} reserveQuality quality={isNum(r.nox) ? noxQuality(r.nox) : null} />
        <ReadingCell label="Mercaptan" value={r.mercPpm} unit="ppm" reserveQuality quality={isNum(r.mercPpm) ? mercQuality(r.mercPpm) : null} />
      </Grid>
      {isNum(r.vocIndex) && <VocIndexDisclaimer />}
    </>
  );
}

// The VOC Index is not a concentration and readers will assume it is one unless
// told otherwise: 100 is the sensor's own learned "normal" for the space it is
// in, so the number only means something relative to that sensor's recent
// history. Shown next to the reading rather than buried in docs.
function VocIndexDisclaimer() {
  return (
    <Alert severity="info" sx={{ mt: 2 }}>
      <strong>About the VOC Index:</strong> this is a relative index from 1 to 500,
      not a concentration in ppm. 100 is the baseline this sensor has learned for
      its own surroundings, so higher means more volatile organic compounds than
      has been normal here recently, and lower means cleaner. The baseline adapts
      over roughly 12 hours and restarts when the sensor is power-cycled, so
      compare readings over time from one sensor rather than between sensors.
    </Alert>
  );
}

// ---------------------------------------------------------------------------

function ReadingsChart({ history }) {
  const labels = history.map((h) => formatTime(h.time));

  const sharedXaxis = {
    categories: labels,
    labels: {
      show: true,
      rotate: 0,
      hideOverlappingLabels: true,
      style: { fontSize: '10px' },
    },
    tickAmount: 6,
  };

  const sharedChart = {
    animations: { enabled: true, dynamicAnimation: { speed: 500 } },
    toolbar: { show: false },
  };

  // Particles chart — PM1.0 + PM2.5 + PM10 share left axis
  const pmOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: '\u00b5g/m\u00b3' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const pmSeries = [
    { name: 'PM1.0', data: history.map((h) => h.pm1) },
    { name: 'PM2.5', data: history.map((h) => h.pm25) },
    { name: 'PM10', data: history.map((h) => h.pm10) },
  ];

  // Particle counts chart
  const pcOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: 'particles / 100cm\u00b3' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const pcSeries = [
    { name: '>0.3\u00b5m', data: history.map((h) => h.pc03) },
    { name: '>0.5\u00b5m', data: history.map((h) => h.pc05) },
    { name: '>1.0\u00b5m', data: history.map((h) => h.pc10) },
    { name: '>2.5\u00b5m', data: history.map((h) => h.pc25) },
    { name: '>5.0\u00b5m', data: history.map((h) => h.pc50) },
    { name: '>10\u00b5m', data: history.map((h) => h.pc100) },
  ];

  // CO2 + VOC chart — multi-axis, since CO2 (~hundreds of ppm), VOC ppm
  // (fractions of a ppm) and the VOC Index (1..500) live on very different
  // scales. A given sensor reports either the ppm channel (PTQS1005) or the
  // index (SEN66/AirPlus), never both, so only one VOC series ever has data.
  const co2Options = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: [
      { seriesName: 'CO2', title: { text: 'CO2 (ppm)' }, min: 0 },
      { seriesName: 'VOC', title: { text: 'VOC (ppm)' }, min: 0, opposite: true },
      { seriesName: 'VOC Index', title: { text: 'VOC Index' }, min: 0, opposite: true },
    ],
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
    colors: ['#FF6B6B', '#4ECDC4', '#8E7CC3'],
  });

  const co2Series = [
    { name: 'CO2', data: history.map((h) => h.co2) },
    { name: 'VOC', data: history.map((h) => h.voc) },
    { name: 'VOC Index', data: history.map((h) => h.vocIndex) },
  ];

  // Environment chart — temp + humidity share left axis
  const envOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: '\u00b0C / %' },
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const envSeries = [
    { name: 'Temperature', data: history.map((h) => h.temp) },
    { name: 'Humidity', data: history.map((h) => h.humidity) },
  ];

  if (history.length < 2) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, color: 'text.secondary' }}>
        <Typography variant="body2">Waiting for more data points...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Mass Concentration (PM1.0 / PM2.5 / PM10)
      </Typography>
      <Chart type="line" series={pmSeries} options={pmOptions} height={180} />

      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        CO2 &amp; VOC
      </Typography>
      <Chart type="line" series={co2Series} options={co2Options} height={180} />

      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Particle Counts by Size
      </Typography>
      <Chart type="line" series={pcSeries} options={pcOptions} height={200} />

      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Environment
      </Typography>
      <Chart type="line" series={envSeries} options={envOptions} height={180} />
    </Box>
  );
}

// ---------------------------------------------------------------------------

export default function SensorReadings({ serialClient, busyOperation }) {
  const [readings, setReadings] = useState(null);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null); // null | 'needs-update' | 'warming-up' | 'error'
  const [lastUpdate, setLastUpdate] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [view, setView] = useState('grid');

  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const addToHistory = useCallback((data) => {
    setHistory((prev) => {
      const entry = {
        time: new Date(),
        pm1: data.pm1 || 0,
        pm25: data.pm25,
        pm10: data.pm10,
        co2: data.co2,
        temp: data.temp,
        humidity: data.humidity,
        voc: data.voc,
        vocIndex: data.vocIndex,
        pc03: data.pc03 || 0,
        pc05: data.pc05 || 0,
        pc10: data.pc10 || 0,
        pc25: data.pc25 || 0,
        pc50: data.pc50 || 0,
        pc100: data.pc100 || 0,
      };
      const next = [...prev, entry];
      return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
    });
  }, []);

  // Poll sensor readings every 2s
  useEffect(() => {
    const fetchReadings = async () => {
      if (busyOperation) return;
      try {
        const data = await serialClient.readings();
        setReadings(data);
        setLastUpdate(new Date());
        setStatus(null);
        addToHistory(data);
      } catch (err) {
        const reason = err.reason || 'error';
        setStatus(reason);
        if (reason === 'needs-update' && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    };

    fetchReadings();
    pollRef.current = setInterval(fetchReadings, 2000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [serialClient, busyOperation, addToHistory]);

  // Tick "seconds ago" counter every 1s
  useEffect(() => {
    tickRef.current = setInterval(() => {
      if (lastUpdate) {
        setSecondsAgo(Math.floor((Date.now() - lastUpdate.getTime()) / 1000));
      }
    }, 1000);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [lastUpdate]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6">Air Quality</Typography>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, v) => { if (v) setView(v); }}
              size="small"
            >
              <ToggleButton value="grid" sx={{ px: 1, py: 0.25 }}>
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="chart" sx={{ px: 1, py: 0.25 }}>
                <TimelineIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {/* Transient status lives in the fixed-height header row so it never
              reflows the readings below it. */}
          {status === 'warming-up' && (
            <Typography variant="caption" color="text.secondary">Warming up&#8230;</Typography>
          )}
          {status === 'error' && (
            <Typography variant="caption" color="warning.main">Couldn&apos;t read &#8212; retrying&#8230;</Typography>
          )}
          {!status && !readings && (
            <Typography variant="caption" color="text.secondary">Connecting&#8230;</Typography>
          )}
          {!status && lastUpdate && (
            <Typography variant="caption" color="text.secondary">
              Updated: {secondsAgo}s ago
              {history.length > 0 && ` \u00b7 ${history.length} samples`}
            </Typography>
          )}
        </Box>

        {status === 'needs-update' ? (
          <Alert severity="info">
            Live readings require a firmware update. Update your sensor to enable them.
          </Alert>
        ) : view === 'grid' ? (
          // Rendered as a skeleton (— placeholders) from the first paint, so live
          // values fill in place without growing the card or pushing content down.
          <ReadingsGrid readings={readings} />
        ) : (
          <ReadingsChart history={history} />
        )}
      </CardContent>
    </Card>
  );
}

SensorReadings.propTypes = {
  serialClient: PropTypes.object.isRequired,
  busyOperation: PropTypes.string,
};
